import {
  Badge,
  Box,
  BoxProps,
  Flex,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverContentProps,
  PopoverProps,
  PopoverTrigger,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiErrorWarningLine, RiInformationLine } from "@remixicon/react";
import { ReactNode } from "react";

import { PortalWrapper } from "@/common/components/PortalWrapper";

export type InfoIconTooltipProps = PopoverProps & {
  text: ReactNode;
  icon?: ReactNode;
  docsURL?: string;
  renderInPortal?: boolean;
  _content?: PopoverContentProps;
  _icon?: BoxProps;
};

export default function InfoIconTooltip({
  text,
  icon = <RiInformationLine size={16} />,
  docsURL,
  renderInPortal = true,
  _content,
  _icon,
  ...rest
}: InfoIconTooltipProps) {
  const tooltipColor = useColorModeValue("gray.400", "gray.600");
  const tooltipHoverColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Popover trigger="hover" variant="tooltip" {...rest}>
      <PopoverTrigger>
        <Box
          _hover={{ color: tooltipHoverColor }}
          as="span"
          color={tooltipColor}
          display="inline-block"
          role="button"
          {..._icon}
        >
          {icon}
        </Box>
      </PopoverTrigger>
      <PortalWrapper usePortal={renderInPortal}>
        <PopoverContent maxW="sm" whiteSpace="normal" {..._content}>
          <PopoverArrow />
          <PopoverBody fontSize="sm" whiteSpace="normal">
            <Box whiteSpace="normal">{text}</Box>
            {docsURL && (
              <Link
                display="block"
                fontSize="sm"
                href={docsURL}
                mt={2}
                target="_blank"
              >
                Learn more
              </Link>
            )}
          </PopoverBody>
        </PopoverContent>
      </PortalWrapper>
    </Popover>
  );
}

type WarningBadgeTooltipProps = Omit<InfoIconTooltipProps, "icon"> & {
  label?: string;
};

export function WarningBadgeTooltip({
  label,
  size = "md",
  ...props
}: WarningBadgeTooltipProps) {
  return (
    <InfoIconTooltip
      {...props}
      icon={
        <Badge colorScheme="orange" size={size} variant="subtle">
          <Flex alignItems="center" gap={1}>
            <RiErrorWarningLine size={16} />
            {label && <Text>{label}</Text>}
          </Flex>
        </Badge>
      }
    />
  );
}
